import { TableProps } from 'antd/es/table';
import { ContainerOutlined, EditOutlined } from '@ant-design/icons';
import { Space, Table, TableColumnsType, Typography } from 'antd';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';
import {
    AdditionalCharge,
    getIrelandTaxCodeLabel,
    getIrelandTaxCodePercents,
    ListAdditionalChargesSortParams,
} from 'store/invoice-config/types';
import { SortDirection, toAntOrder } from 'utils/tableHelpers';
import Tooltip from 'antd/es/tooltip';
import { ReactNode } from 'react';

type Props = Omit<TableProps<AdditionalCharge>, 'dataSource' | 'columns' | 'rowKey'> & {
    data?: AdditionalCharge[];
    showActions?: boolean;
    currencySymbol: string;
    actions?: {
        onEdit?: (charge: AdditionalCharge) => void;
        onArchive?: (charge: AdditionalCharge) => void;
        onUnarchive?: (charge: AdditionalCharge) => void;
    };
    currentSortParam?: ListAdditionalChargesSortParams;
    currentSortDirection?: SortDirection;
    configuredAdditionalChargeId?: string | null;
    // Required for when we show the table in the customer additional charges tab
    archivedAtTenantLevelProductCodes?: Set<string>;
};

const AdditionalChargesTable = ({
    data,
    showActions,
    currencySymbol,
    actions,
    currentSortParam,
    currentSortDirection,
    configuredAdditionalChargeId,
    archivedAtTenantLevelProductCodes,
    ...rest
}: Props) => {
    const columns: TableColumnsType<AdditionalCharge> = [
        {
            title: 'Product Code',
            key: 'PRODUCT_CODE',
            sortOrder: currentSortParam === 'PRODUCT_CODE' ? toAntOrder(currentSortDirection) : null,
            sorter: true,
            render: (_, record) => {
                const dataTestId = 'product-code-row';

                if (!record.productCode) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy copyText={record.productCode} dataTestId={dataTestId}>
                        {record.productCode}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'Description',
            key: 'DESCRIPTION',
            sortOrder: currentSortParam === 'DESCRIPTION' ? toAntOrder(currentSortDirection) : null,
            sorter: true,
            render: (_, record) => {
                const dataTestId = 'description-row';

                if (!record.description) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return <Typography.Text data-testid={dataTestId}>{record.description}</Typography.Text>;
            },
        },
        {
            title: 'Nominal Code',
            key: 'NOMINAL_CODE',
            sortOrder: currentSortParam === 'NOMINAL_CODE' ? toAntOrder(currentSortDirection) : null,
            sorter: true,
            render: (_, record) => {
                const dataTestId = 'nominal-code-row';

                if (!record.nominalCode) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy copyText={record.nominalCode} dataTestId={dataTestId}>
                        {record.nominalCode}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'Tax Code',
            key: 'TAX_CODE',
            sortOrder: currentSortParam === 'TAX_CODE' ? toAntOrder(currentSortDirection) : null,
            sorter: true,
            align: 'left',
            render: (_, record) => {
                const dataTestId = 'tax-code-row';
                const percents = getIrelandTaxCodePercents(record.taxCode);
                const tooltip = getIrelandTaxCodeLabel(record.taxCode);
                const formatted = `${record.taxCode} - ${percents}%`;
                return (
                    <Tooltip title={tooltip}>
                        <Typography.Text data-testid={dataTestId}>{formatted}</Typography.Text>
                    </Tooltip>
                );
            },
        },
        {
            title: `Amount (${currencySymbol})`,
            key: 'AMOUNT',
            align: 'right',
            sortOrder: currentSortParam === 'AMOUNT' ? toAntOrder(currentSortDirection) : null,
            sorter: true,
            render: (_, record) => {
                const dataTestId = 'amount-row';

                if (record.amount == null) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return <Typography.Text data-testid={dataTestId}>{record.amount.toFixed(2)}</Typography.Text>;
            },
        },
        {
            title: 'Percentage (%)',
            key: 'PERCENTAGE',
            sortOrder: currentSortParam === 'PERCENTAGE' ? toAntOrder(currentSortDirection) : null,
            sorter: true,
            align: 'right',
            render: (_, record) => {
                const dataTestId = 'percentage-row';

                if (record.percentage == null) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <Typography.Text style={{ whiteSpace: 'nowrap' }} data-testid={dataTestId}>
                        {record.percentage.toFixed(2)}
                    </Typography.Text>
                );
            },
        },
        {
            title: 'Actions',
            key: 'ACTIONS',
            align: 'right',
            width: 1,
            render: (_, record) => {
                const dataTestId = 'actions-row';
                const isArchived = record.archived;
                const isConfiguredCharge = record.id === configuredAdditionalChargeId;
                const actionButtons: ReactNode[] = [];

                if (actions?.onEdit) {
                    actionButtons.push(
                        <IconButton
                            icon={<EditOutlined />}
                            tooltipText="Edit"
                            onClick={() => actions.onEdit?.(record)}
                        />
                    );
                }

                if (actions?.onArchive && actions.onUnarchive) {
                    if (isArchived) {
                        const isArchivedAtTenantLevel = archivedAtTenantLevelProductCodes?.has(record.productCode);

                        actionButtons.push(
                            <IconButton
                                icon={<ContainerOutlined />}
                                tooltipText="Unarchive"
                                disabled={isArchivedAtTenantLevel}
                                disabledText="This charge is archived at the tenant level and cannot be restored here. Unarchive it from Invoice Settings first."
                                onClick={() => actions?.onUnarchive?.(record)}
                            />
                        );
                    } else {
                        actionButtons.push(
                            <IconButton
                                icon={<ContainerOutlined />}
                                tooltipText="Archive"
                                disabled={isConfiguredCharge}
                                disabledText="This charge cannot be archived while it is set as the default in General Settings. Select a different default charge first."
                                onClick={() => actions?.onArchive?.(record)}
                            />
                        );
                    }
                }

                return (
                    <Space size={8} data-testid={dataTestId}>
                        {actionButtons}
                    </Space>
                );
            },
        },
    ];

    const filteredColumns = showActions ? columns : columns.filter((c) => c.key !== 'ACTIONS');

    return (
        <StyledTableWrapper className="no-last-row-border">
            <Table<AdditionalCharge>
                rowKey="id"
                dataSource={data}
                columns={filteredColumns}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};

export default AdditionalChargesTable;
